import { useEffect, useRef, useState } from "react";
import GlobalSVG from "../../utils/GlobalSVG";
import deleteIcon from "../../assets/delete.png";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import API from "../../utils/API";
import Swal from "sweetalert2";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import ServiceCategoryBadges from "../serviceCategoryBadges";

// --- Car silhouettes for the checklist images ---
import rightCar from "../../assets/right-car.png";
import leftCar from "../../assets/left-car.png";
import internalCar from "../../assets/internal-car.png";
import upCar from "../../assets/up-car.png";
import backCar from "../../assets/back-car.png";
import frontCar from "../../assets/front-car.png";

const mockData = [];

// Base structure of a part with 3 options
const emptyPart = () => ({
  name: "",
  options: [
    { description: '', code: '', quantity: 1, price: '', selected: false },
    { description: '', code: '', quantity: 1, price: '', selected: false },
    { description: '', code: '', quantity: 1, price: '', selected: false },
  ],
});

export default function Services(props) {
  const [data, setData] = useState(mockData);
  const [totalCost, setTotalCost] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [adjustedTotalCost, setAdjustedTotalCost] = useState('');
  const [totalHours, setTotalHours] = useState(0);
  const [prevConclusionDate, setPrevConclusionDate] = useState(new Date());
  const [scheduledDate, setScheduledDate] = useState(new Date());
  const [PRICE_PER_HOUR, SET_PRICE_PER_HOUR] = useState(0); // via API
  const [expandedRow, setExpandedRow] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(0);
  const [serviceSubStatus, setServiceSubStatus] = useState("");
  const [bossApprovalStatus, setBossApprovalStatus] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isQuoteVerified, setIsQuoteVerified] = useState(false);
  const [generatedFile, setGeneratedFile] = useState('');
  const [isDirectExecution, setIsDirectExecution] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [detailedChecklist, setDetailedChecklist] = useState([]); // mandatory items + Bom/Regular/Ruim/Sim/Não
  const [checklistImages, setChecklistImages] = useState([]); // NEW: images from backend
  const [userRole, setUserRole] = useState(null);
  const isLocked = serviceStatus >= 2;
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
      if (storedUser?.role) setUserRole(storedUser.role);
    } catch (e) {}
  }, []);

  useEffect(() => {
    API.getParameters(API.cancelToken())
      .then((response) => {
        if (response.data.valor_homem_hora === null || response.data.valor_homem_hora === undefined) {
          window.location.href = '/manage/parameters';
        } else {
          SET_PRICE_PER_HOUR(Number(response.data.valor_homem_hora) || 0)
        }
      })
      .catch((error) => {
        console.error('Error fetching parameters:', error);
        window.location.href = '/manage/parameters';
      });
  }, []);

  const userEdited = useRef(false);
  useEffect(() => {
    if (userEdited.current) {
      setIsQuoteVerified(false);
      userEdited.current = false;
    }
  }, [data, totalCost, adjustedTotalCost, totalHours, prevConclusionDate, scheduledDate]);

  useEffect(() => {
    if (id) {
      Swal.fire({
        title: 'Carregando dados',
        text: 'Aguarde um momento',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => {
          Swal.showLoading()
        }
      });

      API.getServiceStatus(API.cancelToken(), id).then((res) => {
        if (res) {
          setServiceStatus(res.data.status);
          setServiceSubStatus(res.data.sub_status || "");
          setBossApprovalStatus(res.data.boss_approval_status || null);
        }
      }).catch(err => {
        console.error(err);
        Swal.fire({
          title: 'Erro ao carregar status',
          text: 'Não foi possível carregar o status do serviço',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      });

      API.getChecklistCheckout(API.cancelToken(), id).then((res) => {
        const items = res.data.items || [];
        const result = items.map(item => {
          const hasNewShape = Array.isArray(item.parts) && item.parts.every(p => Array.isArray(p.options));
          const parts = hasNewShape
            ? item.parts.map(p => ({
              name: p.name || "",
              options: (p.options || []).map(o => ({
                service_parts_id: o.service_parts_id ?? null,
                description: o.description || o.descricao || "",
                code: o.code || "",
                quantity: o.quantity || 1,
                price: toCurrencyInput(o.price),
                selected: !!o.selected,
                received: !!o.received,
                installed: !!o.installed,
              }))
            }))
            : [
              {
                name: "",
                options: (item.parts || []).length
                  ? (item.parts || []).map(p => ({
                    service_parts_id: p.service_parts_id ?? null,
                    description: p.description || p.descricao || "",
                    code: p.code || "",
                    quantity: p.quantity || 1,
                    price: toCurrencyInput(p.price),
                    selected: !!p.selected,
                    received: !!p.received,
                    installed: !!p.installed,
                  }))
                  : emptyPart().options,
              },
            ];

          return {
            service_id: id,
            servico: item.service_item_title || "",
            observacao: item.service_item_annotation || "",
            parts,
            hht: item.hht || "",
            total: Number(item.price) || 0,
          };
        });

        setData(result);

        const service_items = res.data;
        if (service_items) {
          setChecklist(service_items.checklist || {});
          setDetailedChecklist(Array.isArray(service_items.detailed_checklist) ? service_items.detailed_checklist : []);
          setChecklistImages(Array.isArray(service_items.images) ? service_items.images : []); // NEW
          setServiceCategories(service_items.service_categories || []);
          setTotalCost(service_items.final_price || 0);
          setTotalHours(service_items.total_hours || 0);
          setAdjustedTotalCost(toCurrencyInput(service_items.final_price));
          if (service_items.expected_delivery === null) {
            setIsDirectExecution(true);
            setPrevConclusionDate(new Date());
          } else {
            setIsDirectExecution(false);
            setPrevConclusionDate(new Date(service_items.expected_delivery));
          }
          if (service_items.scheduled_date) {
            setScheduledDate(new Date(service_items.scheduled_date));
          }
        }

        Swal.close();
        setInitialLoaded(true);
      }).catch(err => {
        setInitialLoaded(true);
        console.error(err);
        Swal.fire({
          title: 'Erro ao carregar dados',
          text: 'Não foi possível carregar os dados existentes',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      });
    } else {
      API.getChecklistDetail(API.cancelToken(), id).then((res) => {
        console.log("Checklist Detail (sem id)", res);
        console.log(JSON.parse(localStorage.getItem('service_items')));
      });

      const service_items = JSON.parse(localStorage.getItem('service_items'));
      if (service_items && service_items.items) {
        const result = service_items.items.map(service => ({
          service_id: id,
          servico: service.service,
          observacao: service.annotation,
          parts: [emptyPart()],
          hht: service.hht || '',
          total: 0
        }));
        setData(result);
      }
    }
  }, [id]);

  // Progressive "cents typing" formatter used by the money inputs:
  // digits fill in from the right (typing 4200 -> R$ 42,00).
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numbers = String(value).replace(/\D/g, '');
    if (!numbers) return '';
    return (Number(numbers) / 100).toLocaleString('pt-BR', {
      style: "currency",
      currency: "BRL"
    });
  };

  /**
   * Separator-aware money parser. Accepts anything we may receive:
   *   42            -> 42        (number)
   *   "R$ 1.702,24" -> 1702.24   (pt-BR display string: dot=thousands, comma=decimal)
   *   "42.0"        -> 42        (machine/JSON string: dot=decimal)
   *   "42.00"       -> 42
   *
   * IMPORTANT: the previous implementation stripped every non-digit and assumed
   * the last two digits were cents, which silently divided values by 10 on each
   * save/load round-trip (R$ 42,00 -> "42.0" -> R$ 4,20). Never go back to that.
   */
  const parseMoney = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    let s = String(value).trim().replace(/[^\d,.-]/g, '');
    if (!s) return 0;
    if (s.includes(',')) {
      // pt-BR display string: dots are thousand separators, comma is decimal
      s = s.replace(/\./g, '').replace(',', '.');
    }
    // no comma => any dot is already the decimal separator
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };

  // Kept for backwards compatibility with the existing call sites.
  const unformatCurrency = (value) => parseMoney(value);

  // Normalizes any incoming money value into the input's display format.
  const toCurrencyInput = (value) =>
    formatCurrency(String(Math.round(parseMoney(value) * 100)));

  // ======== Parts/Options ========

  const markEdited = () => { userEdited.current = true; };

  const addPart = (serviceIndex) => {
    markEdited();
    const newData = [...data];
    newData[serviceIndex].parts = [...(newData[serviceIndex].parts || []), emptyPart()];
    setData(newData);
    updateServiceTotal(newData, serviceIndex);
  };

  const removePart = (serviceIndex, partIndex) => {
    markEdited();
    const newData = [...data];
    newData[serviceIndex].parts = (newData[serviceIndex].parts || []).filter((_, idx) => idx !== partIndex);
    setData(newData);
    updateServiceTotal(newData, serviceIndex);
  };

  const handlePartFieldChange = (serviceIndex, partIndex, field, value) => {
    markEdited();
    const newData = [...data];
    const parts = [...(newData[serviceIndex].parts || [])];
    parts[partIndex] = { ...parts[partIndex], [field]: value };
    newData[serviceIndex].parts = parts;
    setData(newData);
  };

  const handleOptionChange = (serviceIndex, partIndex, optionIndex, field, value) => {
    markEdited();
    const newData = [...data];
    const parts = [...(newData[serviceIndex].parts || [])];
    const part = { ...parts[partIndex] };
    const opts = [...(part.options || [])];
    opts[optionIndex] = { ...opts[optionIndex], [field]: value };
    part.options = opts;
    parts[partIndex] = part;
    newData[serviceIndex].parts = parts;
    setData(newData);
    updateServiceTotal(newData, serviceIndex);
  };

  const selectOption = (serviceIndex, partIndex, optionIndex) => {
    markEdited();
    const newData = [...data];
    const parts = [...(newData[serviceIndex].parts || [])];
    const part = { ...parts[partIndex] };
    part.options = (part.options || []).map((o, i) => ({ ...o, selected: i === optionIndex }));
    parts[partIndex] = part;
    newData[serviceIndex].parts = parts;
    setData(newData);
    updateServiceTotal(newData, serviceIndex);
  };

  // ======== Hours / Remove service ========

  const handleHhtChange = (index, value) => {
    markEdited();
    const newData = [...data];
    newData[index].hht = value;
    setData(newData);
    updateServiceTotal(newData, index);
  };

  const handleDelete = (index) => {
    const updatedData = data.filter((_, idx) => idx !== index);
    setData(updatedData);
    const hours = updatedData.reduce((acc, item) => acc + (Number(item.hht) || 0), 0);
    const total = updatedData.reduce((acc, item) => acc + (Number(item.total) || 0), 0);
    setTotalHours(hours);
    setTotalCost(total);
    setAdjustedTotalCost(toCurrencyInput(total));
  };

  // ======== Totals ========

  const updateServiceTotal = (serviceData, index) => {
    const service = serviceData[index];

    const partsValue = (service.parts || []).reduce((sum, part) => {
      const sel = (part.options || []).find(o => o.selected);
      if (!sel) return sum;
      const price = Number(unformatCurrency(sel.price)) || 0;
      const qty = Number(sel.quantity) || 1;
      return sum + (price * qty);
    }, 0);

    const hours = Number(service.hht) || 0;
    const rate = Number(PRICE_PER_HOUR) || 0;
    service.total = Number(partsValue + (hours * rate)) || 0;
    serviceData[index] = service;
    setData(serviceData);

    const hoursTotal = serviceData.reduce((acc, item) => acc + (Number(item.hht) || 0), 0);
    const grandTotal = serviceData.reduce((acc, item) => acc + (Number(item.total) || 0), 0);
    setTotalHours(hoursTotal);
    setTotalCost(grandTotal);
    setAdjustedTotalCost(toCurrencyInput(grandTotal));
  };

  const [needsRecalc, setNeedsRecalc] = useState(false);

  useEffect(() => {
    if (!initialLoaded) {
      const hours = data.reduce((acc, item) => acc + (Number(item.hht) || 0), 0);
      const total = data.reduce((acc, item) => acc + (Number(item.total) || 0), 0);
      setTotalHours(hours);
      setTotalCost(total);
      setAdjustedTotalCost(toCurrencyInput(total));
    } else {
      setInitialLoaded(false);
      // Mark that we need recalc once PRICE_PER_HOUR is ready
      if (data.some(s => Number(s.hht) > 0)) {
        setNeedsRecalc(true);
      }
    }
  }, [data]); // eslint-disable-line

  // Recalculate all service totals once PRICE_PER_HOUR is available and data needs it
  useEffect(() => {
    if (!needsRecalc || PRICE_PER_HOUR <= 0 || data.length === 0) return;
    setNeedsRecalc(false);

    const rate = Number(PRICE_PER_HOUR) || 0;
    const recalculated = data.map(service => {
      const partsValue = (service.parts || []).reduce((sum, part) => {
        const sel = (part.options || []).find(o => o.selected);
        if (!sel) return sum;
        const price = Number(unformatCurrency(sel.price)) || 0;
        const qty = Number(sel.quantity) || 1;
        return sum + (price * qty);
      }, 0);
      const hours = Number(service.hht) || 0;
      return { ...service, total: Number(partsValue + (hours * rate)) || 0 };
    });
    const hoursTotal = recalculated.reduce((acc, item) => acc + (Number(item.hht) || 0), 0);
    const grandTotal = recalculated.reduce((acc, item) => acc + (Number(item.total) || 0), 0);
    // Update totals without triggering another setData cycle
    setTotalHours(hoursTotal);
    setTotalCost(grandTotal);
    setAdjustedTotalCost(toCurrencyInput(grandTotal));
    // Use functional update to avoid the [data] useEffect from resetting
    setInitialLoaded(true);
    setData(recalculated);
  }, [needsRecalc, PRICE_PER_HOUR]); // eslint-disable-line

  const handleAdjustedTotalChange = (value) => {
    markEdited();
    const numbers = value.replace(/\D/g, '');
    const formatted = formatCurrency(numbers);
    setAdjustedTotalCost(formatted);
  };

  const toggleRowExpansion = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const validateAllHhtFilled = () => {
    return true;
  };

  // --- Helpers for Vistoria images (annotated car) ---

  const AREA_IMAGE = {
    Direita: rightCar,
    Esquerda: leftCar,
    Topo: upCar,
    Traseira: backCar,
    Dianteira: frontCar,
    Interno: internalCar,
  };

  // Bounding box (in mm) for each annotated car image in the PDF.
  // Canvas itself uses the image's natural dimensions; this box only
  // controls the final printed size (contain-fit, preserving aspect).
  const AREA_PDF_BOX_MM = {
    Direita: { maxW: 130, maxH: 80 },
    Esquerda: { maxW: 130, maxH: 80 },
    Dianteira: { maxW: 110, maxH: 80 },
    Traseira: { maxW: 110, maxH: 80 },
    Topo: { maxW: 70, maxH: 130 },
    Interno: { maxW: 130, maxH: 80 },
  };

  // 96 DPI conversions
  const pxToMm = (px) => px * 0.264583;
  const mmToPx = (mm) => mm / 0.264583;

  async function fetchViaProxyAsDataURL(url) {
    const endpoint = `https://youconnected-f6114d7c794c.herokuapp.com/api/image/base64?url=${encodeURIComponent(url)}`;
    const res = await fetch(endpoint, { method: 'GET', credentials: 'include' });
    if (!res.ok) throw new Error(`Proxy failed: ${res.status}`);
    const json = await res.json();
    if (!json?.data_url) throw new Error('No data_url from proxy');
    return json.data_url;
  }

  function loadImageElFromDataURL(dataUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  function loadImageElDirect(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // requires S3 CORS
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  /**
   * Draw base image at fixed target size (px) and drop pins.
   * Positions were captured around a ~500px width in the UI,
   * so we scale from that reference to the fixed target size.
   */
  // The InputChecklist renders each car image with Tailwind's max-h-[300px]
  // (height capped at 300, width scales with the natural aspect ratio).
  // Clicks are stored in that rendered display pixel space.
  //
  // Here we draw the image at its NATURAL pixel dimensions (no distortion),
  // then map the stored click coordinates from the rendered space into the
  // natural space. The final printed size is decided later by the caller.
  async function buildAnnotatedCar(area, entries, clickHeightPx = 300) {
    const base = AREA_IMAGE[area] || frontCar;

    const img = await loadImageElFromDataURL(base);
    const naturalW = img.naturalWidth || 1;
    const naturalH = img.naturalHeight || 1;

    // Canvas matches the image's natural size, so the base is drawn 1:1
    // and pins are placed in natural pixel coordinates.
    const canvas = document.createElement("canvas");
    canvas.width = naturalW;
    canvas.height = naturalH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, naturalW, naturalH);

    // Rendered size at click time:
    //   max-h-[300px], no width constraint, aspect preserved.
    //   If natural height <= 300, the image was shown at natural size.
    const renderedHeight = Math.min(naturalH, clickHeightPx);
    const renderedWidth = naturalW * (renderedHeight / naturalH);

    // Scale a pin radius / font size proportional to the canvas so they
    // stay visually consistent across very different image dimensions.
    const baseDim = Math.max(naturalW, naturalH);
    const pinRadius = Math.max(6, Math.round(baseDim * 0.018));
    const fontPx = Math.max(10, Math.round(baseDim * 0.03));

    entries.forEach((e, i) => {
      const [xr, yr] = String(e.position || "")
        .split(",")
        .map(s => parseFloat(s.trim()));
      const x = (Number.isFinite(xr) ? xr : 0) * (naturalW / renderedWidth);
      const y = (Number.isFinite(yr) ? yr : 0) * (naturalH / renderedHeight);

      ctx.fillStyle = "#e11d48"; // rose-600
      ctx.beginPath();
      ctx.arc(x, y, pinRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "#fff";
      ctx.font = `bold ${fontPx}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(i + 1), x, y);
    });

    return { dataUrl: canvas.toDataURL("image/png"), wPx: naturalW, hPx: naturalH };
  }

  // --- Helpers: Standardize external checklist photos (S3) ---

  const STD_IMG_MM = {
    landscape: { w: 120, h: 90 },
    portrait: { w: 90, h: 120 },
  };

  async function buildStandardizedExternalImage(url) {
    try {
      // 1) Try proxy → data URL (no CORS needed in browser)
      const dataUrl = await fetchViaProxyAsDataURL(url);
      const img = await loadImageElFromDataURL(dataUrl);

      const isLandscape = img.naturalWidth >= img.naturalHeight;
      const targetMm = isLandscape ? STD_IMG_MM.landscape : STD_IMG_MM.portrait;
      const targetPx = { w: mmToPx(targetMm.w), h: mmToPx(targetMm.h) };

      const canvas = document.createElement('canvas');
      canvas.width = targetPx.w; canvas.height = targetPx.h;
      const ctx = canvas.getContext('2d');

      // white background + contain fit
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, targetPx.w, targetPx.h);
      const ratio = Math.min(targetPx.w / img.naturalWidth, targetPx.h / img.naturalHeight);
      const drawW = Math.round(img.naturalWidth * ratio);
      const drawH = Math.round(img.naturalHeight * ratio);
      const dx = Math.round((targetPx.w - drawW) / 2);
      const dy = Math.round((targetPx.h - drawH) / 2);
      ctx.drawImage(img, dx, dy, drawW, drawH);

      return { dataUrl: canvas.toDataURL('image/jpeg', 0.92), wMm: targetMm.w, hMm: targetMm.h };
    } catch (proxyErr) {
      // 2) Fallback to direct crossOrigin load (works if S3 CORS is configured)
      try {
        const img = await loadImageElDirect(url);
        const isLandscape = img.naturalWidth >= img.naturalHeight;
        const targetMm = isLandscape ? STD_IMG_MM.landscape : STD_IMG_MM.portrait;
        const targetPx = { w: mmToPx(targetMm.w), h: mmToPx(targetMm.h) };

        const canvas = document.createElement('canvas');
        canvas.width = targetPx.w; canvas.height = targetPx.h;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, targetPx.w, targetPx.h);
        const ratio = Math.min(targetPx.w / img.naturalWidth, targetPx.h / img.naturalHeight);
        const drawW = Math.round(img.naturalWidth * ratio);
        const drawH = Math.round(img.naturalHeight * ratio);
        const dx = Math.round((targetPx.w - drawW) / 2);
        const dy = Math.round((targetPx.h - drawH) / 2);
        ctx.drawImage(img, dx, dy, drawW, drawH);

        return { dataUrl: canvas.toDataURL('image/jpeg', 0.92), wMm: targetMm.w, hMm: targetMm.h };
      } catch (directErr) {
        // 3) Final safe placeholder (what you see now)
        const targetMm = STD_IMG_MM.landscape;
        const targetPx = { w: mmToPx(targetMm.w), h: mmToPx(targetMm.h) };
        const canvas = document.createElement("canvas");
        canvas.width = targetPx.w; canvas.height = targetPx.h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#f3f4f6"; ctx.fillRect(0, 0, targetPx.w, targetPx.h);
        ctx.fillStyle = "#374151"; ctx.font = "14px sans-serif"; ctx.textAlign = "center";
        ctx.fillText("Imagem indisponível", targetPx.w / 2, targetPx.h / 2 - 8);
        ctx.fillStyle = "#6b7280"; ctx.font = "11px monospace";
        ctx.fillText((url || "").slice(0, 40), targetPx.w / 2, targetPx.h / 2 + 10);
        return { dataUrl: canvas.toDataURL("image/png"), wMm: targetMm.w, hMm: targetMm.h };
      }
    }
  }

  // ======== Export PDF ========

  const handleExportQuote = async () => {
    if (!validateAllHhtFilled()) return;
    const doc = new jsPDF();

    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;

    doc.setProperties({
      title: 'Orçamento de Serviços',
      author: 'Seu Nome da Empresa'
    });

    // Title + Date
    doc.setFontSize(18);
    doc.text('Orçamento de Serviços', 105, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 105, 30, { align: 'center' });

    // Services Section
    let yOffset = 50;
    doc.setFontSize(12);
    doc.text('Detalhes dos Serviços', 10, yOffset);
    yOffset += 10;

    const servicesData = data.map(service => {
      const selectedParts = (service.parts || []).map(p => {
        const sel = (p.options || []).find(o => o.selected);
        return {
          partName: p.name || '',
          description: sel?.description || 'Sem opção selecionada',
          price: sel?.price || 'R$ 0,00'
        };
      });

      const partsText = selectedParts
        .map(sp => `${sp.partName ? sp.partName + ': ' : ''}${sp.description}`)
        .join('\n');

      const partsPriceText = selectedParts.map(sp => sp.price).join('\n');

      return [
        service.servico,
        service.observacao || 'Sem observações',
        `${service.hht}h`,
        partsText || 'Sem peças selecionadas',
        partsPriceText || 'R$ 0,00',
        (service.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      ];
    });

    doc.autoTable({
      startY: yOffset,
      head: [['Serviço', 'Observação', 'HHT', 'Peças Selecionadas', 'Valor Peças', 'Total do Serviço']],
      headStyles: { fillColor: [56, 158, 13], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
      tableWidth: 'auto',
      body: servicesData,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 40 }, 2: { cellWidth: 20 }, 3: { cellWidth: 40 }, 4: { cellWidth: 25 }, 5: { cellWidth: 25 } }
    });

    // Totals
    const finalYOffset = doc.previousAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text('Resumo do Orçamento', 15, finalYOffset);

    const totalsData = [
      ['Total de Horas de Trabalho', `${totalHours.toFixed(1)}h`],
      ['Valor (Mão de Obra)', adjustedTotalCost],
      ...(isDirectExecution ? [['Execução', 'Direta']]
        : [
            ...(scheduledDate ? [['Data de Início', scheduledDate.toLocaleDateString('pt-BR')]] : []),
            ...(prevConclusionDate ? [['Previsão de Conclusão', prevConclusionDate.toLocaleDateString('pt-BR')]] : [])
          ])
    ];

    doc.autoTable({
      startY: finalYOffset + 5,
      body: totalsData,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: 'bold' } }
    });

    // Parts (all options)
    const partsYOffset = doc.previousAutoTable.finalY + 25;
    doc.setFontSize(12);
    doc.text('Opções de Peças Disponíveis', 10, partsYOffset);

    const allParts = data.flatMap(service =>
      (service.parts || []).flatMap((part, idx) =>
        (part.options || [])
          .filter(o => (o.description || '').trim() !== '')
          .map(o => {
            const qty = Number(o.quantity) || 1;
            const unit = unformatCurrency(o.price);
            const totalPeca = (unit * qty).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
            const row = [
              service.servico,
              part.name || `Peça ${idx + 1}`,
              o.description,
              o.code || '-',
              qty,
              totalPeca,
              o.selected ? 'Selecionada' : 'Opção'
            ];
            row.selected = o.selected;
            return row;
          })
      )
    );

    doc.autoTable({
      startY: partsYOffset + 10,
      head: [['Serviço', 'Peça', 'Descrição da Opção', 'Código', 'Qtd', 'Valor', 'Status']],
      headStyles: { fillColor: [56, 158, 13], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
      body: allParts,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 35 },
        2: { cellWidth: 40 },
        3: { cellWidth: 20 },
        4: { cellWidth: 12 },
        5: { cellWidth: 22 },
        6: { cellWidth: 22 }
      },
      didParseCell: function (data) {
        if (data.row?.raw?.selected) {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // === Checklist Obrigatório (Detailed Checklist with Bom/Regular/Ruim) ===
    let currentY = (doc.previousAutoTable && doc.previousAutoTable.finalY)
      ? doc.previousAutoTable.finalY + 16
      : 50;

    const detailedRows = (Array.isArray(detailedChecklist) ? detailedChecklist : [])
      .filter(d => d && d.item);

    if (detailedRows.length > 0) {
      // Page break if too close to bottom
      if (currentY + 30 > pageHeight - 10) {
        doc.addPage();
        currentY = 20;
      }
      doc.setFontSize(12);
      doc.text("Checklist Obrigatório", 10, currentY);
      currentY += 4;

      doc.autoTable({
        startY: currentY,
        head: [["Item", "Status", "Observação"]],
        headStyles: {
          fillColor: [56, 158, 13],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold",
        },
        body: detailedRows.map(d => [
          d.item || "-",
          d.status || "-",
          d.observation || "-",
        ]),
        theme: "striped",
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 95 },
          1: { cellWidth: 25, halign: "center" },
          2: { cellWidth: 70 },
        },
        // Color the status cell by value (Bom=green, Regular=yellow, Ruim=red, Sim=red, Não=green)
        didParseCell: (data) => {
          if (data.section !== 'body' || data.column.index !== 1) return;
          const v = (data.cell.raw || '').toString().trim().toLowerCase();
          if (v === 'bom' || v === 'não' || v === 'nao') {
            data.cell.styles.fillColor = [220, 252, 231];
            data.cell.styles.textColor = [22, 101, 52];
            data.cell.styles.fontStyle = 'bold';
          } else if (v === 'regular') {
            data.cell.styles.fillColor = [254, 249, 195];
            data.cell.styles.textColor = [133, 77, 14];
            data.cell.styles.fontStyle = 'bold';
          } else if (v === 'ruim' || v === 'sim') {
            data.cell.styles.fillColor = [254, 226, 226];
            data.cell.styles.textColor = [153, 27, 27];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      currentY = (doc.previousAutoTable && doc.previousAutoTable.finalY)
        ? doc.previousAutoTable.finalY + 12
        : currentY + 30;
    }

    // === Vistoria do Carro (Apontamentos / car views with pin numbers) ===
    const hasAnyChecklist = checklist && Object.keys(checklist).length > 0;
    if (hasAnyChecklist) {
      doc.setFontSize(12);
      doc.text("Vistoria do Carro", 10, currentY);
      currentY += 6;

      const areas = Object.keys(checklist || {});
      for (const area of areas) {
        const raw = checklist[area];
        // Support both formats:
        //  - new: array of { title, status, observation, position }
        //  - legacy: object keyed by title (duplicates collide)
        const rawList = Array.isArray(raw)
          ? raw
          : Object.entries(raw || {}).map(([title, v]) => ({ title, ...v }));
        const entries = rawList.map((v, idx) => ({
          idx: idx + 1,
          title: v?.title || "",
          status: v?.status || "-",        // becomes "Descrição" column
          observation: v?.observation || "",
          position: v?.position || "",
        }));

        if (!entries.length) continue;

        // Build annotated image at its natural pixel size
        const built = await buildAnnotatedCar(area, entries);

        // Contain-fit into the area's printed bounding box (in mm)
        const box = AREA_PDF_BOX_MM[area] || { maxW: 110, maxH: 80 };
        const aspect = built.wPx / built.hPx;
        let imgW = box.maxW;
        let imgH = imgW / aspect;
        if (imgH > box.maxH) {
          imgH = box.maxH;
          imgW = imgH * aspect;
        }
        const centerX = (pageWidth - imgW) / 2;

        // Page break if needed
        if (currentY + imgH + 14 > pageHeight - 10) {
          doc.addPage();
          currentY = 20;
        }

        // Area header
        doc.setFontSize(11);
        doc.text(`Área: ${area}`, 10, currentY);
        currentY += 5;

        // Image centered
        doc.addImage(built.dataUrl, "PNG", centerX, currentY, imgW, imgH);
        currentY += imgH + 4;

        // Table with renamed headers: Local, Descrição, Observação
        doc.autoTable({
          startY: currentY,
          head: [["#", "Local", "Descrição", "Observação"]],
          headStyles: {
            fillColor: [56, 158, 13],
            textColor: [255, 255, 255],
            fontSize: 9,
            fontStyle: "bold",
          },
          body: entries.map(e => [
            e.idx,
            e.title || "-",
            e.status || "-",
            e.observation || "-",
          ]),
          theme: "striped",
          styles: { fontSize: 8, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 8 },
            1: { cellWidth: 60 },
            2: { cellWidth: 28 },
            3: { cellWidth: 95 },
          },
        });

        currentY = doc.previousAutoTable.finalY + 12;
      }
    }

    // === NEW: Checklist Photos (standardized sizes) ===
    if (Array.isArray(checklistImages) && checklistImages.length) {
      // Start a new section
      if (currentY + 20 > pageHeight - 10) {
        doc.addPage();
        currentY = 20;
      }
      doc.setFontSize(12);
      doc.text("Imagens da Vistoria", 10, currentY);
      currentY += 8;

      for (let i = 0; i < checklistImages.length; i++) {
        const imgObj = checklistImages[i];
        const url = imgObj?.image_url || imgObj?.url || "";
        if (!url) continue;

        console.log("Processing checklist image:", url);
        const std = await buildStandardizedExternalImage(url);

        // Page break if needed
        if (currentY + std.hMm + 12 > pageHeight - 10) {
          doc.addPage();
          currentY = 20;
        }

        const x = (pageWidth - std.wMm) / 2; // center
        doc.addImage(std.dataUrl, "JPG", x, currentY, std.wMm, std.hMm);
        currentY += std.hMm + 6;

        // Optional: small caption with index or date
        // const createdAt = imgObj?.created_at ? new Date(imgObj.created_at).toLocaleString('pt-BR') : '';
        // doc.setFontSize(9); doc.text(`Imagem ${i+1}${createdAt ? ` • ${createdAt}` : ''}`, 10, currentY);
        // currentY += 6;
      }
    }

    // Save & mark verified
    doc.save(`orcamento_${new Date().toISOString().split('T')[0]}.pdf`);
    const base64 = doc.output('datauristring');
    setGeneratedFile(base64.split(',')[1]);
    setIsQuoteVerified(true);
    console.log(`Generated PDF Base64: ${base64.split(',')[1]}`);
  };

  // ======== Boss approval (técnica) ========

  // Build the payload that handleSubmit normally sends. We reuse this when
  // we need to persist the in-memory parts data without sending the quote.
  const buildSubmissionData = () => ({
    services: data.map(service => {
      const parts = (service.parts || []).map(p => ({
        name: p.name || "",
        options: (p.options || []).map(o => ({
          description: o.description,
          code: o.code,
          quantity: parseInt(o.quantity) || 1,
          price: unformatCurrency(o.price) || 0,
          selected: !!o.selected,
        })),
      }));
      const legacy_pecas = (service.parts || []).flatMap((p, pIdx) =>
        (p.options || []).map(o => ({
          description: o.description,
          code: o.code,
          quantity: parseInt(o.quantity) || 1,
          price: unformatCurrency(o.price) || 0,
          selected: !!o.selected,
          group_index: pIdx,
        }))
      );
      return {
        servico: service.servico,
        observacao: service.observacao,
        hht: service.hht,
        parts,
        pecas: legacy_pecas,
        total: service.total,
      };
    }),
    totalHours,
    calculatedTotal: totalCost,
    adjustedTotal: unformatCurrency(adjustedTotalCost),
  });

  // Silently persist current state to the backend without sending to client.
  // Used as a safety-save before workflow transitions so admins/bosses don't
  // lose any pending edits to parts/services.
  const persistCurrentSilently = () => {
    if (!data || data.length === 0) return Promise.resolve();
    return API.putServiceItem(
      API.cancelToken(),
      buildSubmissionData(),
      id,
      isDirectExecution ? null : prevConclusionDate,
      "NO",
      isDirectExecution ? null : scheduledDate
    );
  };

  const handleBossApprove = () => {
    Swal.fire({
      title: "Liberar para Compras?",
      text: "A equipe de compras poderá iniciar o orçamento e a aquisição das peças.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#389E0D",
      confirmButtonText: "Liberar para Compras",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({ title: "Liberando...", showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      // Save any pending data first so admin (who may have already typed parts)
      // doesn't lose work between this click and the next interaction.
      persistCurrentSilently()
        .catch((err) => {
          // Don't block the approval on a save failure — log and continue.
          console.warn("[handleBossApprove] silent save failed, proceeding to approve anyway", err);
        })
        .then(() => API.postBossApproval(API.cancelToken(), id, "approve"))
        .then(() => {
          setBossApprovalStatus("approved");
          Swal.fire("Liberado!", "Serviço enviado para a equipe de compras.", "success");
        })
        .catch((err) => {
          const msg = err?.response?.data?.error || "Não foi possível liberar para compras.";
          Swal.fire("Erro", msg, "error");
        });
    });
  };

  const handleBossReject = () => {
    Swal.fire({
      title: "Rejeitar análise do mecânico?",
      text: "Informe o motivo para que o mecânico possa corrigir.",
      input: "textarea",
      inputPlaceholder: "Motivo da rejeição...",
      inputAttributes: { "aria-label": "Motivo da rejeição" },
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Rejeitar",
      cancelButtonText: "Cancelar",
      inputValidator: (value) => {
        if (!value || !value.trim()) return "Informe o motivo da rejeição.";
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({ title: "Rejeitando...", showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      API.postBossApproval(API.cancelToken(), id, "reject", result.value)
        .then(() => {
          setBossApprovalStatus("rejected");
          Swal.fire({
            title: "Rejeitado",
            text: "O mecânico será notificado para refazer a análise.",
            icon: "info",
          }).then(() => navigate("/services"));
        })
        .catch((err) => {
          const msg = err?.response?.data?.error || "Não foi possível rejeitar.";
          Swal.fire("Erro", msg, "error");
        });
    });
  };

  // ======== Complete / Send ========

  const completeService = () => {
    if (isSubmitting) return;

    Swal.fire({
      title: 'Verificando pendências...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    // Defensive precheck: refuse to close the OS if any service item is still
    // pending (not FINISHED) or any selected part hasn't been marked as installed.
    API.getServiceItems(API.cancelToken(), id)
      .then((res) => {
        const items = res.data || [];
        const pendingItems = items.filter((it) => it.status !== 'FINISHED');
        const pendingParts = [];
        items.forEach((it) => {
          const parts = it?.service_items?.pecas || [];
          parts.forEach((p) => {
            if (p && p.selected && !p.installed) {
              pendingParts.push(`${it.service_item_title}: ${p.descricao || p.codigo || '(peça sem nome)'}`);
            }
          });
        });

        if (pendingItems.length > 0 || pendingParts.length > 0) {
          Swal.close();
          const itemsHtml = pendingItems.length
            ? `<p><strong>Serviços não finalizados (${pendingItems.length}):</strong></p><ul style="text-align:left;padding-left:20px">${pendingItems.map(i => `<li>${i.service_item_title}</li>`).join('')}</ul>`
            : '';
          const partsHtml = pendingParts.length
            ? `<p style="margin-top:8px"><strong>Peças não instaladas (${pendingParts.length}):</strong></p><ul style="text-align:left;padding-left:20px">${pendingParts.map(p => `<li>${p}</li>`).join('')}</ul>`
            : '';
          Swal.fire({
            title: 'Existem pendências',
            html: `${itemsHtml}${partsHtml}<p style="margin-top:12px">Conclua todos os serviços e marque as peças instaladas antes de finalizar.</p>`,
            icon: 'warning',
            confirmButtonText: 'Ok',
          });
          return;
        }

        Swal.fire({
          title: 'Tem certeza que deseja finalizar o serviço?',
          text: 'Essa ação não pode ser desfeita.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, finalizar',
          cancelButtonText: 'Cancelar'
        }).then((result) => {
          if (!result.isConfirmed) return;
          API.putServiceStatus(API.cancelToken(), id, 3)
            .then(() => {
              Swal.fire({
                title: 'Serviço finalizado com sucesso',
                text: 'Aguarde a confirmação do cliente para prosseguir com o serviço.',
                icon: 'success',
                confirmButtonText: 'Ok'
              });
              setIsSubmitting(false);
              window.location.href = '/services';
            })
            .catch((err) => {
              // Surface the real backend message (e.g. pending items/parts 409)
              // instead of a generic error.
              const msg = err?.response?.data?.error || 'Tente novamente mais tarde';
              Swal.fire({
                title: 'Erro ao finalizar serviço',
                text: msg,
                icon: 'error',
                confirmButtonText: 'Ok'
              });
              console.log(err);
              setIsSubmitting(false);
            });
        });
      })
      .catch((err) => {
        Swal.close();
        console.error('[completeService] precheck failed', err);
        Swal.fire('Erro', 'Não foi possível verificar pendências. Tente novamente.', 'error');
      });
  };

  const handleSubmit = (salvar = false) => {
    if (isSubmitting) return;
    if (!validateAllHhtFilled()) return;
    if (!isQuoteVerified && !salvar) return;

    setIsSubmitting(true);

    if (salvar) {
      Swal.fire({
        title: 'Salvando serviço...',
        text: 'Aguarde um momento',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => {
          Swal.showLoading()
        }
      });
    } else {
      Swal.fire({
        title: 'Enviando orçamento...',
        text: 'Aguarde um momento',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => {
          Swal.showLoading()
        }
      });
    }

    const submissionData = {
      services: data.map(service => {
        const parts = (service.parts || []).map(p => ({
          name: p.name || "",
          options: (p.options || []).map(o => ({
            description: o.description,
            code: o.code,
            quantity: parseInt(o.quantity) || 1,
            price: unformatCurrency(o.price) || 0,
            selected: !!o.selected,
          })),
        }));

        const legacy_pecas = (service.parts || []).flatMap((p, pIdx) =>
          (p.options || []).map(o => ({
            description: o.description,
            code: o.code,
            quantity: parseInt(o.quantity) || 1,
            price: unformatCurrency(o.price) || 0,
            selected: !!o.selected,
            group_index: pIdx,
          }))
        );

        const selectedPecas = (service.parts || [])
          .map(p => (p.options || []).find(o => o.selected))
          .filter(Boolean)
          .map(o => ({
            ...o,
            price: unformatCurrency(o.price) || 0,
          }));

        return {
          servico: service.servico,
          observacao: service.observacao,
          hht: service.hht,
          parts,
          pecas: legacy_pecas,
          selectedPecas,
          total: service.total
        };
      }),
      totalHours,
      calculatedTotal: totalCost,
      adjustedTotal: unformatCurrency(adjustedTotalCost),
      pdf_base64: generatedFile,
    };

    API.putServiceItem(API.cancelToken(), submissionData, id, isDirectExecution ? null : prevConclusionDate, salvar ? "NO" : "YES", isDirectExecution ? null : scheduledDate)
      .then(res => {
        const emailSent = res?.data?.email_sent;
        const emailError = res?.data?.email_error;

        if (!salvar && !emailSent) {
          // Os dados foram salvos, mas o e-mail para o cliente não saiu.
          // Avisar explicitamente em vez de dizer "enviado com sucesso".
          Swal.fire({
            title: 'Orçamento salvo, mas não enviado',
            text: emailError
              ? `Não foi possível enviar o e-mail ao cliente: ${emailError}`
              : 'Não foi possível enviar o e-mail ao cliente. Verifique o e-mail cadastrado e tente novamente.',
            icon: 'warning',
            confirmButtonText: 'Ok'
          });
        } else {
          Swal.fire({
            title: salvar ? 'Serviço salvo com sucesso.' : 'Orçamento enviado com sucesso!',
            text: salvar ? '' : 'Aguarde a confirmação do cliente para prosseguir com o serviço.',
            icon: 'success',
            confirmButtonText: 'Ok'
          }).then(() => {
            // Enviar o orçamento encerra a etapa de compras: o usuário volta
            // para a home. "Salvar" é rascunho, então permanece na tela.
            if (!salvar) navigate('/dashboard');
          });
        }

        if (!salvar) {
          localStorage.removeItem('client');
          localStorage.removeItem('client_id');
          localStorage.removeItem('vehicle_id');
          localStorage.removeItem('vehicle');
          setIsSubmitting(false);
        } else {
          setIsSubmitting(false);
        }
      })
      .catch(err => {
        // Mostrar a mensagem real do backend em vez de um texto genérico.
        const msg = err?.response?.data?.error
          || err?.response?.data?.details
          || 'Tente novamente mais tarde';
        Swal.fire({
          title: 'Erro ao salvar serviços',
          text: msg,
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        console.error('[handleSubmit] falhou', err);
        setIsSubmitting(false);
      });
  };

  const togglePartReceived = (serviceIndex, partIndex, optionIndex) => {
    const opt = data[serviceIndex]?.parts?.[partIndex]?.options?.[optionIndex];
    if (!opt || opt.service_parts_id == null) return;
    const newValue = !opt.received;

    // optimistic update
    setData(prev => {
      const copy = [...prev];
      const svc = { ...copy[serviceIndex] };
      const parts = [...(svc.parts || [])];
      const part = { ...parts[partIndex] };
      const opts = [...(part.options || [])];
      opts[optionIndex] = { ...opts[optionIndex], received: newValue };
      part.options = opts;
      parts[partIndex] = part;
      svc.parts = parts;
      copy[serviceIndex] = svc;
      return copy;
    });

    API.setPartReceived(API.cancelToken(), opt.service_parts_id, newValue).catch(() => {
      Swal.fire("Erro", "Não foi possível atualizar a peça.", "error");
      // revert
      setData(prev => {
        const copy = [...prev];
        const svc = { ...copy[serviceIndex] };
        const parts = [...(svc.parts || [])];
        const part = { ...parts[partIndex] };
        const opts = [...(part.options || [])];
        opts[optionIndex] = { ...opts[optionIndex], received: !newValue };
        part.options = opts;
        parts[partIndex] = part;
        svc.parts = parts;
        copy[serviceIndex] = svc;
        return copy;
      });
    });
  };

  const handleReleaseToMechanic = () => {
    Swal.fire({
      title: "Liberar peças para o mecânico?",
      text: "O mecânico poderá iniciar o serviço com as peças já recebidas.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#389E0D",
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({ title: "Liberando...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      API.serviceTransition(API.cancelToken(), id, "release_to_mechanic")
        .then((res) => {
          setServiceSubStatus(res.data.new_substatus || "EM REPARO");
          Swal.fire("Liberado!", "O serviço foi enviado para o mecânico.", "success");
        })
        .catch(() => Swal.fire("Erro", "Não foi possível liberar.", "error"));
    });
  };

  const canShowPartsPanel = serviceStatus >= 2 && ["COMPRADOR", "ADMIN", "BOSS"].includes(userRole);
  const canReleaseToMechanic = canShowPartsPanel && (!serviceSubStatus || serviceSubStatus === "AGUARDANDO PEÇA");

  return (
    <div className="flex py-6 px-4 sm:px-6 md:px-8 lg:px-11 flex-col items-center self-stretch bg-white rounded-sm shadow-sm max-w-full">
      <div className="flex py-4 px-2 justify-between items-center self-stretch flex-wrap gap-3">
        <p className="text-2xl font-medium">Serviços necessários</p>
        <ServiceCategoryBadges codes={serviceCategories} size="sm" />
      </div>

      {/* BOSS / ADMIN review banner — when there's a pending approval */}
      {(userRole === "BOSS" || userRole === "ADMIN") && bossApprovalStatus === "pending" && (
        <div className="w-full mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-amber-900">Revisão da análise do mecânico</h3>
              <p className="text-sm text-amber-800 mt-0.5">
                Confira a avaliação técnica, HHT e a necessidade de peças. Em seguida, libere para a equipe de compras ou rejeite com observações.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Banner shown when buyer/etc. opens the screen while approval is still pending */}
      {!(userRole === "BOSS" || userRole === "ADMIN") && bossApprovalStatus === "pending" && (
        <div className="w-full mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
          <p className="text-sm text-yellow-800 font-medium">
            Aguardando aprovação do chefe da oficina para iniciar a montagem do orçamento.
          </p>
        </div>
      )}

      {/* Parts receiving panel (for COMPRADOR/ADMIN/BOSS after client approval) */}
      {canShowPartsPanel && (
        <div className="w-full mb-4 p-4 rounded-lg bg-blue-50 border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Recebimento de Peças</h3>
            <span className="text-xs px-2 py-1 rounded-full bg-white text-gray-600 border border-gray-200">
              {serviceSubStatus || "AGUARDANDO PEÇA"}
            </span>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Marque as peças à medida que forem chegando. O mecânico pode começar o serviço com as peças já recebidas.
          </p>
          <div className="space-y-3">
            {data.map((service, serviceIndex) => {
              const selectedParts = (service.parts || []).flatMap((p, partIndex) =>
                (p.options || []).map((o, optIdx) => ({ p, o, partIndex, optIdx }))
              ).filter(({ o }) => o.selected);
              if (selectedParts.length === 0) return null;
              return (
                <div key={serviceIndex} className="bg-white border border-gray-200 rounded p-3">
                  <p className="text-sm font-medium text-gray-800 mb-2">{service.servico}</p>
                  <ul className="space-y-1.5">
                    {selectedParts.map(({ o, partIndex, optIdx }, k) => (
                      <li key={k} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!o.received}
                          disabled={o.service_parts_id == null}
                          onChange={() => togglePartReceived(serviceIndex, partIndex, optIdx)}
                          className="h-4 w-4"
                        />
                        <span className={`text-sm ${o.received ? "text-gray-500 line-through" : "text-gray-800"}`}>
                          {o.description || "(sem descrição)"}
                          {o.code ? ` (cód: ${o.code})` : ""}
                          {o.quantity > 1 ? ` × ${o.quantity}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          {canReleaseToMechanic && (
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={handleReleaseToMechanic}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#389E0D] text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
              >
                Liberar peças para o mecânico
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Lock editing when client has already approved (status >= 2) */}
      {serviceStatus >= 2 && (
        <div className="w-full mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-300 flex items-center gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-yellow-800 font-medium">
            Este orçamento já foi aprovado pelo cliente. Não é possível fazer alterações.
          </p>
        </div>
      )}

      <div className="flex flex-col items-end gap-6 self-stretch w-full">
        {data.map((service, serviceIndex) => (
          <div key={serviceIndex} className="w-full border rounded-md overflow-hidden">
            <div className="flex items-center bg-gray-50 p-4">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-6 gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <p className="font-medium">{service.servico}</p>
                  <p className="text-sm text-gray-500">{service.observacao}</p>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-500">Peças Selecionadas</p>
                  <div className="text-sm">
                    {(service.parts || []).map((p, i) => {
                      const sel = (p.options || []).find(o => o.selected);
                      return (
                        <div key={i}>
                          <span className="text-gray-500">{p.name || `Peça ${i + 1}`}:</span>{" "}
                          <span>{sel?.description || 'Nenhuma'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-500">{userRole === "MECANICO" ? "Horas" : "HHT"}</p>
                  <select
                    value={service.hht ?? ""}
                    onChange={(e) => handleHhtChange(serviceIndex, e.target.value)}
                    disabled={userRole === "COMPRADOR" || isLocked}
                    className={`w-full sm:w-24 p-1 border border-gray-300 rounded-sm bg-white ${(userRole === "COMPRADOR" || isLocked) ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  >
                    <option value="">-</option>
                    {Array.from({ length: 40 }, (_, i) => (i + 1) * 0.5).map((v) => (
                      <option key={v} value={v}>{String(v).replace('.', ',')}</option>
                    ))}
                  </select>
                </div>

                {userRole !== "MECANICO" && (
                <div className="text-center">
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="font-medium">
                    {(service.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </p>
                </div>
                )}

                <div className="flex justify-end items-center gap-4 col-span-1 sm:col-span-1">
                  <button
                    onClick={() => toggleRowExpansion(serviceIndex)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {expandedRow === serviceIndex ? 'Fechar' : 'Opções de peça'}
                  </button>
                  {!isLocked && (
                  <img
                    src={deleteIcon}
                    alt="Remover serviço"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => handleDelete(serviceIndex)}
                  />
                  )}
                </div>
              </div>
            </div>

            {expandedRow === serviceIndex && (
              <div className="p-4 bg-white">
                <div className="flex justify-between items-center mb-4">
                  <p className="font-medium">Peças deste serviço</p>
                </div>

                {(service.parts || []).map((part, partIndex) => {
                  const isRequested = String(part.name || "").startsWith("Solicitação do mecânico");
                  // A OS aprovada pelo cliente fica somente-leitura, mas a peça que o
                  // mecânico pediu DEPOIS da aprovação precisa ser precificada para
                  // virar orçamento complementar. Só ela é destravada; o que o cliente
                  // já aprovou continua intocável.
                  const partLocked = isLocked && !isRequested;
                  return (
                  <div key={partIndex} className={`mb-6 border rounded-md p-4 ${isRequested ? "border-amber-300 bg-amber-50" : ""}`}>
                    {isRequested && (
                      <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-200 text-amber-800">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        Peça solicitada pelo mecânico — precifique e inclua no orçamento
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="text"
                        value={part.name || ""}
                        onChange={(e) => !partLocked && handlePartFieldChange(serviceIndex, partIndex, 'name', e.target.value)}
                        readOnly={partLocked}
                        className={`w-full sm:w-80 p-2 border border-gray-300 rounded-sm ${partLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        placeholder={`Nome da peça ${partIndex + 1} (opcional)`}
                      />
                      {!partLocked && (
                      <button
                        onClick={() => removePart(serviceIndex, partIndex)}
                        className="px-3 py-2 rounded-md bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                      >
                        Remover peça
                      </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {(part.options || []).map((opt, optionIndex) => (
                        <div
                          key={optionIndex}
                          className={`p-4 border rounded-md ${opt.selected ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                        >
                          <p className="text-sm text-gray-500 mb-2">
                            Opção {optionIndex + 1} {opt.selected ? '(Selecionada)' : ''}
                          </p>

                          <div className="mb-2">
                            <p className="text-sm text-gray-500">Descrição</p>
                            <input
                              type="text"
                              value={opt.description}
                              onChange={(e) => !partLocked && handleOptionChange(serviceIndex, partIndex, optionIndex, 'description', e.target.value)}
                              readOnly={partLocked}
                              className={`w-full p-2 border border-gray-300 rounded-sm mt-1 ${partLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                              placeholder="Descrição"
                            />
                          </div>

                          <div className="mb-2">
                            <p className="text-sm text-gray-500">Código</p>
                            <input
                              type="text"
                              value={opt.code}
                              onChange={(e) => !partLocked && handleOptionChange(serviceIndex, partIndex, optionIndex, 'code', e.target.value)}
                              readOnly={partLocked}
                              className={`w-full p-2 border border-gray-300 rounded-sm mt-1 ${partLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                              placeholder="Código"
                            />
                          </div>

                          <div className="mb-2">
                            <p className="text-sm text-gray-500">Quantidade</p>
                            <input
                              type="number"
                              value={opt.quantity}
                              onChange={(e) => !partLocked && handleOptionChange(serviceIndex, partIndex, optionIndex, 'quantity', e.target.value)}
                              readOnly={partLocked}
                              className={`w-full p-2 border border-gray-300 rounded-sm mt-1 ${partLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                              min="1"
                              step="1"
                            />
                          </div>

                          {userRole !== "MECANICO" && (
                          <div className="mb-3">
                            <p className="text-sm text-gray-500">Preço Unitário da Peça</p>
                            <input
                              type="text"
                              value={opt.price}
                              onChange={(e) =>
                                !partLocked && handleOptionChange(
                                  serviceIndex,
                                  partIndex,
                                  optionIndex,
                                  'price',
                                  formatCurrency(e.target.value)
                                )
                              }
                              readOnly={partLocked}
                              className={`w-full p-2 border border-gray-300 rounded-sm mt-1 ${partLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                              placeholder="R$"
                            />
                          </div>
                          )}

                          <button
                            onClick={() => !partLocked && selectOption(serviceIndex, partIndex, optionIndex)}
                            disabled={partLocked}
                            className={`w-full p-2 rounded-md ${partLocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : opt.selected ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {opt.selected ? 'Selecionada' : 'Selecionar esta opção'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
                <div className="flex justify-between items-center mb-4">
                  <p className="font-medium"></p>
                {!isLocked && (
                <button
                    onClick={() => addPart(serviceIndex)}
                    className="px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Adicionar peça
                  </button>
                )}
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="flex flex-col gap-4 w-full">
          <div className="w-full border border-gray-200 rounded-lg p-5 bg-gray-50">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Modo de execução</p>
            <div className="flex gap-3 mb-4">
              <button
                type="button"
                onClick={() => !isLocked && setIsDirectExecution(false)}
                disabled={isLocked}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                  isLocked ? "opacity-60 cursor-not-allowed" :
                  !isDirectExecution
                    ? "border-[#389E0D] bg-green-50 text-[#389E0D]"
                    : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 011 1v3h3a1 1 0 110 2H7a1 1 0 01-1-1V8a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Agendar execução
              </button>
              <button
                type="button"
                onClick={() => !isLocked && setIsDirectExecution(true)}
                disabled={isLocked}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                  isLocked ? "opacity-60 cursor-not-allowed" :
                  isDirectExecution
                    ? "border-[#389E0D] bg-green-50 text-[#389E0D]"
                    : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                Execução imediata
              </button>
            </div>

            {!isDirectExecution && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-600">Data de início</label>
                  <DatePicker
                    selected={scheduledDate}
                    onChange={(date) => { if (!isLocked) { markEdited(); setScheduledDate(date); } }}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent ${isLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    disabled={isLocked}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-600">Previsão de conclusão</label>
                  <DatePicker
                    selected={prevConclusionDate}
                    onChange={(date) => { if (!isLocked) { markEdited(); setPrevConclusionDate(date); } }}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent ${isLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                    minDate={scheduledDate || new Date()}
                    dateFormat="dd/MM/yyyy"
                    disabled={isLocked}
                  />
                </div>
              </div>
            )}

            {isDirectExecution && (
              <p className="text-sm text-gray-500 pt-2 border-t border-gray-200">
                O serviço será iniciado imediatamente, sem agendamento prévio.
              </p>
            )}
          </div>
          {userRole !== "MECANICO" && (
          <div className="flex justify-end gap-6 flex-wrap">
            <div className="flex items-start gap-2">
              <p className="text-base">Total de horas: </p>
              <p className="text-base font-bold">{totalHours.toFixed(1)}h</p>
            </div>
            <div className="flex items-start gap-2">
              <p className="text-base">Valor total calculado: </p>
              <p className="text-base font-bold">
                {totalCost ? totalCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : ''}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <p className="text-base">Valor final: </p>
              <input
                type="text"
                value={adjustedTotalCost}
                onChange={(e) => !isLocked && handleAdjustedTotalChange(e.target.value)}
                readOnly={isLocked}
                className={`w-full sm:w-32 p-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-bold ${isLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                placeholder="R$"
              />
            </div>
          </div>
          )}
        </div>
      </div>

      {
        serviceStatus && serviceStatus === 2 ?
          <div className="flex px-3 py-6 h-auto w-full flex-row flex-wrap justify-end items-center gap-4 self-stretch">
            <div
              className="flex h-10 px-4 py-2 justify-center items-center border rounded shadow-sm cursor-pointer text-white bg-[#389E0D] border-[#389E0D] mt-10 w-full sm:w-auto min-h-[50px]"
              onClick={() => completeService(true)}
            >
              <p className="ml">Finalizar Serviço</p>
            </div>
          </div>
          :
          <div className="flex px-3 py-6 h-auto w-full flex-row flex-wrap justify-end items-center gap-4 self-stretch">
            <div
              className="flex h-10 px-2 py-4 justify-center items-center border border-[#389E0D] rounded shadow-sm cursor-pointer text-[#389E0D] w-full sm:w-auto"
              onClick={() => navigate('/create/service?step=4&id=' + id)}
            >
              {GlobalSVG.arrowLeft()}
              <p className="ml-2">Voltar</p>
            </div>

            {/* Approval CTAs — only when reviewing the mechanic's analysis */}
            {(userRole === "BOSS" || userRole === "ADMIN") && bossApprovalStatus === "pending" && (
              <>
                <div
                  className="inline-flex h-10 px-4 py-2 justify-center items-center border-2 border-red-500 rounded shadow-sm cursor-pointer text-red-600 hover:bg-red-50 transition-colors w-full sm:w-auto"
                  onClick={handleBossReject}
                >
                  <p className="font-medium">Rejeitar</p>
                </div>
                <div
                  className="inline-flex h-10 px-4 py-2 justify-center items-center gap-2 border bg-[#389E0D] border-[#389E0D] rounded shadow-sm cursor-pointer text-white hover:bg-green-700 transition-colors w-full sm:w-auto"
                  onClick={handleBossApprove}
                >
                  <p className="font-medium">Liberar para Compras</p>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </>
            )}

            {/* Buyer-only actions: hidden for BOSS, and hidden for anyone while a boss approval is still pending */}
            {userRole !== "BOSS" && bossApprovalStatus !== "pending" && (
              <>
                <div
                  className="flex h-10 px-4 py-2 justify-center items-center border border-blue-600 rounded shadow-sm cursor-pointer text-blue-600 w-full sm:w-auto"
                  onClick={handleExportQuote}
                >
                  {GlobalSVG.download()}
                  <p className="ml-2">Exportar Orçamento</p>
                </div>
                <div
                  className="flex h-10 px-4 py-2 justify-center items-center border border-blue-600 rounded shadow-sm cursor-pointer text-blue-600 w-full sm:w-auto"
                  onClick={() => handleSubmit(true)}
                >
                  <p className="ml">Salvar</p>
                </div>
                <div
                  className={`relative group flex h-10 px-2 py-4 justify-center items-center ${isQuoteVerified ? 'bg-[#389E0D] border-[#389E0D] cursor-pointer' : 'bg-[#eae9e9] border-[#FFFFFF] cursor-not-allowed'
                    } gap-2.5 border rounded shadow-sm w-full sm:w-auto`}
                  onClick={() => {
                    if (isQuoteVerified) handleSubmit(false);
                  }}
                >
                  {isQuoteVerified ? GlobalSVG.arrowRight() : ''}
                  <p className={`${isQuoteVerified ? 'text-[#FFF]' : 'text-[#c0bdbd]'}`}>Enviar Orçamento Para Cliente</p>

                  {!isQuoteVerified && (
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-max max-w-[200px] px-3 py-2 text-xs text-white bg-black rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 hidden sm:block">
                      Baixe e verifique o orçamento antes de enviar para o cliente.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
      }
    </div>
  );
}
